import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarDays, ClipboardList, History, ShieldCheck, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

export type SettingsTab = 'general' | 'reporting' | 'permissions' | 'surveys' | 'admin' | 'audit'

const TAB_BASE = 'px-4 py-2 text-sm font-semibold border-b-2'

/**
 * Tab strip shared by /settings, /settings/reporting, /settings/permissions,
 * /settings/surveys, /settings/admin and /settings/audit. Each tab is only rendered for the roles
 * that can reach it; each page keeps its own redirect, because this strip is
 * presentation only.
 */
export default function SettingsTabs({ active }: { active: SettingsTab }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = !!user?.isAdmin
  const isReportingAdmin = !!user?.isReportingAdmin
  const canManageSurveys = !!user?.canManageSurveys

  const tab = (id: SettingsTab, path: string, label: ReactNode) => (
    <button
      key={id}
      type="button"
      aria-current={id === active ? 'page' : undefined}
      aria-disabled={id === active || undefined}
      onClick={id === active ? undefined : () => navigate(path)}
      className={id === active
        ? `${TAB_BASE} border-danone-cyan text-slate-800`
        : `${TAB_BASE} border-transparent text-slate-500 hover:text-slate-700`}
    >
      {label}
    </button>
  )

  const withIcon = (Icon: LucideIcon, text: string) => (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" />
      {text}
    </span>
  )

  return (
    <div className="border-b border-slate-200 mb-6">
      <nav className="flex gap-1">
        {tab('general', '/settings', 'General')}
        {isReportingAdmin && tab('reporting', '/settings/reporting', withIcon(CalendarDays, 'Status Reporting'))}
        {isAdmin && tab('permissions', '/settings/permissions', withIcon(Users, 'Permissions'))}
        {canManageSurveys && tab('surveys', '/settings/surveys', withIcon(ClipboardList, 'Surveys'))}
        {isAdmin && tab('admin', '/settings/admin', withIcon(ShieldCheck, 'Admin'))}
        {isAdmin && tab('audit', '/settings/audit', withIcon(History, 'Audit'))}
      </nav>
    </div>
  )
}
